import React from 'react';
import { Link } from 'react-router-dom';
import MemberPhotoSlider from './MemberPhotoSlider';
import { getUserImages } from '../../utils/userImages';
import VerificationBadges from './VerificationBadges';
import './ProfileMemberCard.css';

const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age > 0 ? age : null;
};

const formatHeight = (height) => {
  if (!height && height !== 0) return 'N/A';
  const parts = String(height).split('.');
  if (parts.length === 2) {
    return `${parts[0]}'${parts[1]}"`;
  }
  return String(height);
};

const formatGender = (gender) => {
  if (!gender) return 'N/A';
  const g = String(gender).toLowerCase();
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  return gender;
};

/**
 * Classic Hindu Sanjog member card — photo on top, pink info panel below.
 */
const ProfileMemberCard = ({
  member,
  to,
  onClick,
  nestedSlider = false,
  className = '',
}) => {
  if (!member) return null;

  const age = calculateAge(member.dob);
  const ageLabel = age != null ? `${age} Yrs` : 'N/A';
  const images = getUserImages(member);
  const isPremiumMember = Boolean(
    member.is_premium === true ||
    member.is_premium === 1 ||
    member.is_premium === '1'
  );

  const inner = (
    <>
      <div className="member-photo">
        {isPremiumMember && (
          <span className="member-premium-badge" title="Premium Member">
            <span className="member-premium-badge__crown" aria-hidden>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17h18l-1.5-9-4.5 4L12 5l-3 7-4.5-4L3 17zm2 2h14v2H5v-2z" />
              </svg>
            </span>
            Premium
          </span>
        )}
        <MemberPhotoSlider
          images={images}
          alt={member.name}
          imageClassName="member-image"
          nested={nestedSlider}
        />
      </div>
      <div className="member-info">
        <h5>{member.name || 'Member'}</h5>
        <div className="member-id-gender">
          <span className="member-id">HS{member.user_id}</span>
        </div>
        <VerificationBadges
          compact
          verifyMobile={member.verify_mobile}
          verifyEmail={member.verify_email}
        />
        <div className="member-details">
          <div className="detail-row">
            <span className="detail-label">Age:</span>
            <span className="detail-value">{ageLabel}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Height:</span>
            <span className="detail-value">{formatHeight(member.height)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Gender:</span>
            <span className="detail-value">{formatGender(member.gender)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Caste:</span>
            <span className="detail-value">{member.caste || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Location:</span>
            <span className="detail-value">
              {member.curr_location || member.country || 'India'}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const classes = `member-card ${isPremiumMember ? 'member-card--premium' : ''} ${className}`.trim();

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick} style={{ color: 'white' }}>
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={classes}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(e); } : undefined}
      style={onClick ? { cursor: 'pointer', color: 'white' } : { color: 'white' }}
    >
      {inner}
    </div>
  );
};

export default ProfileMemberCard;
